import { useState } from 'react';
import { BookOpen, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GLOSSARY_TERMS, GlossaryTerm } from '@/lib/glossary-terms';

const CATEGORY_LABELS: Record<GlossaryTerm['category'], string> = {
  cee: 'CEE',
  commercial: 'Commercial',
  administratif: 'Administratif',
  technique: 'Technique',
};

const CATEGORY_COLORS: Record<GlossaryTerm['category'], string> = {
  cee: 'bg-primary/10 text-primary',
  commercial: 'bg-blue-500/10 text-blue-600',
  administratif: 'bg-amber-500/10 text-amber-600',
  technique: 'bg-purple-500/10 text-purple-600',
};

interface GlossaryDialogProps {
  trigger?: React.ReactNode;
}

export function GlossaryDialog({ trigger }: GlossaryDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<GlossaryTerm['category'] | 'all'>('all');

  const terms = Object.values(GLOSSARY_TERMS);
  
  const filteredTerms = terms.filter(term => {
    const matchesSearch = search === '' || 
      term.term.toLowerCase().includes(search.toLowerCase()) ||
      term.shortDefinition.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const categories: Array<GlossaryTerm['category'] | 'all'> = ['all', 'cee', 'commercial', 'administratif', 'technique'];

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <BookOpen className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Glossaire CEE
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un terme..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className="h-7 text-xs"
              >
                {cat === 'all' ? 'Tous' : CATEGORY_LABELS[cat]}
              </Button>
            ))}
          </div>

          {/* Terms list */}
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {filteredTerms.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun terme trouvé
                </p>
              ) : (
                filteredTerms.map((term, index) => (
                  <div 
                    key={index} 
                    className="p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-foreground">{term.term}</h3>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs shrink-0 ${CATEGORY_COLORS[term.category]}`}
                      >
                        {CATEGORY_LABELS[term.category]}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {term.shortDefinition}
                    </p>
                    {term.longDefinition && (
                      <p className="text-sm text-foreground/80 border-t pt-2 mt-2">
                        {term.longDefinition}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
